/* eslint-disable fp/no-mutation */
/* eslint-disable fp/no-mutating-methods */
import React, { ReactElement, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import Alert from '@mui/material/Alert'
import Avatar from '@mui/material/Avatar'
import Box from '@mui/material/Box'
import Chip from '@mui/material/Chip'
import Divider from '@mui/material/Divider'
import Drawer from '@mui/material/Drawer'
import IconButton from '@mui/material/IconButton'
import LinearProgress from '@mui/material/LinearProgress'
import Snackbar from '@mui/material/Snackbar'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'

import theme from 'src/theme'
import { useAppDispatch, useAppSelector } from 'src/store/hooks'
import axios from 'src/utilities/http-client'
import {
  fetchLearningPaths,
  selectLearningPaths,
  selectLearningPathsStatus,
  fetchNewYorkLearningPathCourses,
  selectNewYorkLearningPathCourses,
  selectNewYorkLearningPathCoursesStatus,
  NewYorkCourseStatus,
} from 'src/v3/profile/profile.slice'
import {
  CancelIcon,
  CorrectIcon,
  IncorrectIcon,
  LearningPathIcon,
  ResetIcon,
} from 'src/v3/core/icons'
import ConfirmAction from 'src/v3/components/confirm-action'
import { Container } from '@mui/material'
import Loading from 'src/v3/components/loading'

export default (): ReactElement => {
  let navigate = useNavigate()
  let dispatch = useAppDispatch()

  let learningPaths = useAppSelector(selectLearningPaths)
  let learningPathsStatus = useAppSelector(selectLearningPathsStatus)
  let courses = useAppSelector(selectNewYorkLearningPathCourses)
  let coursesStatus = useAppSelector(selectNewYorkLearningPathCoursesStatus)

  let [selected, setSelected] = useState<string>('')
  let [dialog, setDialog] = useState<string>('')
  let [successMessage, setSuccessMessage] = useState<string>('')
  let [errorMessage, setErrorMessage] = useState<string>('')

  learningPaths = learningPaths.filter(
    (lp) => lp.category.toLowerCase() === 'new york',
  )

  let selectedPath = learningPaths.find((lp) => lp.id === selected)

  let coursesByStatus =
    selectedPath?.courseIDs.reduce(
      (dict, courseID) => {
        let course = courses[selected]?.find((c) => c.id === courseID)
        if (course) {
          let courseStatus =
            courses[selected]?.find((c) => c.id === courseID)
              ?.mandatoryStatus || 'Other'
          // eslint-disable-next-line no-param-reassign
          dict[courseStatus] ??= []
          dict[courseStatus].push(course)
          dict[courseStatus].sort(
            (a, b) =>
              (a.topicArea || '').localeCompare(b.topicArea || '') ||
              a.name.localeCompare(b.name),
          )
        }
        return dict
      },
      {} as Record<string, NewYorkCourseStatus[]>,
    ) || {}

  let statusOrder = ['Mandatory', 'Core Content', 'Other'].filter((s) =>
    Object.keys(coursesByStatus).includes(s),
  )

  let pathCompletion =
    !selectedPath || !selected
      ? 0
      : // eslint-disable-next-line no-unsafe-optional-chaining
        (courses[selected]?.filter((c) => c.status === 'Complete').length /
          selectedPath.courseIDs.length) *
        100

  useEffect(() => {
    if (learningPathsStatus === 'uninitialized') {
      dispatch(fetchLearningPaths())
    }
  }, [learningPathsStatus])

  useEffect(() => {
    if (selected && (!courses[selected] || courses[selected].length === 0)) {
      setSelected('')
      setErrorMessage('No Course Information Found')
    }
  }, [selected])

  return (
    <>
      <Container maxWidth="xl">
        <div className="flex items-center justify-center my-2">
          <Avatar sx={{ bgcolor: theme.palette.primary.main }}>
            <LearningPathIcon />
          </Avatar>
          <Typography
            variant="h4"
            className="pl-2"
            style={{
              fontFamily: 'smileAndWave',
            }}
          >
            New York Learning Paths
          </Typography>
        </div>
        <Typography textAlign="center" color="text.secondary" sx={{ mb: 2 }}>
          View learning paths specific to New York state
        </Typography>
        <Alert severity="info" className="mb-7">
          Online CME training is limited to 50% of the total CME hours, required
          by the program, when a participant uses them as self-study.
          <br />
          a) To utilize online education programs for 100% self-study education
          the program must be administered by the agency, and/or the affiliate
          course sponsor and approved by the Bureau of EMS prior to the start of
          the program.
          <br />
          b) An individual CME participant who is not registered with an agency
          approved to utilize 100% online education will be denied
          recertification for exceeding the maximum allowable CME hours for
          self-study.
          <br />
          <br />
          FOAMfrat, LLC is approved for 100% online CME content, however
          agencies must be approved by the New York Bureau of EMS Central
          Office.
        </Alert>
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'flex-start',
            gap: 2,
          }}
        >
          {learningPaths.map((lp) => {
            let completed = courses[lp.id]?.every(
              (c) => c.status === 'Complete',
            )
            return (
              <Box
                key={lp.id}
                sx={{
                  flex: '0 0 240px',
                  border: `1px solid ${theme.palette.primary.dark}`,
                  borderRadius: 2,
                  filter: completed ? 'grayscale(0)' : 'grayscale(0.8)',
                  '&:hover': {
                    cursor: 'pointer',
                    filter: 'grayscale(0)',
                  },
                }}
                onClick={() => {
                  if (
                    coursesStatus[lp.id] === 'uninitialized' ||
                    !coursesStatus[lp.id]
                  ) {
                    dispatch(fetchNewYorkLearningPathCourses(lp.id))
                      .then(() => setSelected(lp.id))
                      .catch((err) =>
                        setErrorMessage(
                          err?.message || 'Error Finding Courses',
                        ),
                      )
                  } else {
                    setSelected(lp.id)
                  }
                }}
              >
                {completed && (
                  <Box sx={{ height: 0 }}>
                    <CorrectIcon
                      sx={{
                        position: 'relative',
                        left: -12,
                        top: -12,
                        color: theme.palette.success.main,
                        backgroundColor: 'black',
                        borderRadius: 12,
                      }}
                    />
                  </Box>
                )}
                <Typography textAlign="center" sx={{ p: 1 }}>
                  {lp.name}
                </Typography>
                <Divider
                  sx={{
                    borderColor: theme.palette.primary.dark,
                    '&:hover': {
                      borderColor: 'inherit',
                    },
                  }}
                />
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    pt: 1,
                    pb: 2,
                    gap: 1,
                  }}
                >
                  <Typography>{lp.courseIDs.length} Courses</Typography>
                  <img
                    src={`https://d29e3091pe8039.cloudfront.net/public/${lp.imageName}`}
                    style={{ height: 160, width: 160, flexGrow: 0 }}
                    alt={lp.name}
                  />
                </Box>
              </Box>
            )
          })}
        </Box>
        <Drawer
          anchor="right"
          open={!!selected}
          onClose={() => setSelected('')}
          PaperProps={{
            sx: {
              mt: 8,
              backgroundImage: 'none',
              bgcolor: 'background.default',
              p: 1,
              borderLeft: `2px solid ${theme.palette.divider}`,
            },
          }}
        >
          <Box
            sx={{
              mb: 0.5,
              px: 0.5,
              py: 1,
              display: 'flex',
              alignItems: 'center',
              gap: 1,
            }}
          >
            <Box sx={{ flex: '0 0 fit-content' }}>
              <Avatar
                src={`https://d29e3091pe8039.cloudfront.net/public/${selectedPath?.imageName}`}
              />
            </Box>
            <Box sx={{ flex: '1 1 fit-content' }}>{selectedPath?.name}</Box>
            <Box sx={{ flex: '0 0 fit-content' }}>
              <IconButton
                onClick={() => setSelected('')}
                sx={{
                  '&:hover': { bgcolor: theme.palette.background.paper },
                }}
              >
                <CancelIcon />
              </IconButton>
            </Box>
          </Box>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              columnGap: 1,
              mb: 1,
              px: 0.5,
            }}
          >
            <LinearProgress
              sx={{ flex: '1 1 50px' }}
              variant="determinate"
              value={pathCompletion}
            />
            <Typography variant="body2">
              {(Math.round(pathCompletion * 100) / 100).toFixed(0)}% Complete
            </Typography>
            <Tooltip title="Reset Progress">
              <IconButton
                color="error"
                size="small"
                sx={{
                  ml: -1,
                  '&:hover': { bgcolor: theme.palette.background.paper },
                }}
                onClick={() => {
                  setDialog('reset')
                }}
              >
                <ResetIcon />
              </IconButton>
            </Tooltip>
            <ConfirmAction
              executeAction={() =>
                axios
                  .put(
                    `/api/profile/learning-path/reset?learningPathID=${selected}`,
                  )
                  .then(() => dispatch(fetchLearningPaths()))
                  .then(() =>
                    dispatch(fetchNewYorkLearningPathCourses(selected)),
                  )
                  .then(() =>
                    setSuccessMessage('Learning Path successfully reset!'),
                  )
              }
              open={dialog}
              setOpen={setDialog}
              openText="reset"
              title="Confirm Progress Reset"
              text="Are you sure you want to reset progress for this learning path?"
              onFailure={(err) =>
                setErrorMessage(err.message || 'Error Resetting Learning Path')
              }
            />
          </Box>
          <Box>
            <Alert severity="info" className="max-w-3xl mx-4">
              Psychiatric and Behavioral Emergencies counts towards both Core
              Content and Mandatory hours.
              <br />
              <br />
              Additional Continuing Education Hours may be completed using
              courses outside of the this learning path.
            </Alert>
          </Box>
          {statusOrder.map((mandatoryStatus, i, arr) => (
            <Box
              key={mandatoryStatus}
              sx={{ mt: i === 0 ? 5 : 0, pb: i === arr.length - 1 ? 5 : 2 }}
            >
              <Typography
                sx={{ fontSize: 18, fontStyle: 'italic', ml: 1, mb: 0.5 }}
              >
                {mandatoryStatus}
              </Typography>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 1,
                  overflow: 'scroll',
                  mb: i === statusOrder.length - 1 ? 6 : undefined,
                }}
              >
                {coursesByStatus[mandatoryStatus || ''].map((c) =>
                  !c ? (
                    <></>
                  ) : (
                    <Box
                      key={c.id}
                      sx={{
                        p: 0.5,
                        border: `1px solid ${theme.palette.primary.dark}`,
                        borderRadius: 20,
                        bgcolor: 'black',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        '&:hover': {
                          cursor: 'pointer',
                          bgcolor: theme.palette.background.paper,
                        },
                      }}
                      onClick={() => navigate(`/self-paced/course/${c.id}`)}
                    >
                      <Box sx={{ flex: '0 0 fit-content' }}>
                        <Avatar
                          sx={{ width: 42, height: 42 }}
                          src={`https://d29e3091pe8039.cloudfront.net/public/${c.imageName}`}
                        />
                      </Box>
                      <Box sx={{ flex: '1 0 fit-content' }}>
                        {c.name}
                        {c.topicArea && (
                          <Typography
                            color="text.secondary"
                            sx={{ fontSize: 12 }}
                          >
                            {c.topicArea}
                          </Typography>
                        )}
                      </Box>
                      <Box sx={{ flex: '1 0 1px' }}></Box>
                      {c.status === 'Complete' ? (
                        <Chip
                          sx={{ width: 112, mr: '5px', color: 'black' }}
                          color="success"
                          variant="filled"
                          label="Completed"
                          icon={<CorrectIcon />}
                        />
                      ) : (
                        <Chip
                          sx={{ width: 112, mr: '5px', bgcolor: 'black' }}
                          color="error"
                          variant="outlined"
                          label="Incomplete"
                          icon={<IncorrectIcon />}
                        />
                      )}
                    </Box>
                  ),
                )}
              </Box>
              {i === arr.length - 1 && (
                <Alert severity="info" className="max-w-3xl mx-4">
                  Upon completion of all content, it is the providers
                  responsibility to forward certificates and NYS forms to their
                  Agency CME Coordinator/Training Officer. It is the
                  responsibility of both the student and their Agency CME
                  Coordinator to complete and submit the appropriate
                  Recertification Forms to the NYS Department of Health, Bureau
                  of EMS no greater than forty-five (45) days prior to the
                  provider's certification expiration date.
                  <br />
                  <br />
                  FOAMfrat, LLC is not responsible for paperwork submission to
                  the NYS DOH, nor are we responsible for students who fail to
                  complete CME requirements within the prescribed time frame.
                </Alert>
              )}
            </Box>
          ))}
        </Drawer>
        <Snackbar open={!!successMessage} onClose={() => setSuccessMessage('')}>
          <Alert
            variant="filled"
            onClose={() => setSuccessMessage('')}
            severity="success"
          >
            {successMessage}
          </Alert>
        </Snackbar>
        <Snackbar open={!!errorMessage} onClose={() => setErrorMessage('')}>
          <Alert
            variant="filled"
            onClose={() => setErrorMessage('')}
            severity="error"
          >
            {errorMessage}
          </Alert>
        </Snackbar>
      </Container>
      <Loading entities={[learningPathsStatus]} />
    </>
  )
}
